import {
  styled,
  Card,
  Box,
  IconButton,
  Chip,
  keyframes,
  alpha,
  Typography,
} from "@mui/material";

// Animations
export const gradientSlide = keyframes`
  0% {
    background-position: 200% center;
  }
  100% {
    background-position: -200% center;
  }
`;

export const pulseSubtle = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
`;

// Message card
export const CleanMessageCard = styled(Card)(({ theme, isUser = false }) => ({
  background: isUser
    ? theme.palette.mode === "dark"
      ? alpha(theme.palette.background.paper, 0.6)
      : alpha(theme.palette.grey[100], 0.8)
    : "transparent",
  border: "none",
  boxShadow: "none",
  borderRadius: isUser ? "16px 4px 4px 16px" : "16px",
  transition: "all 0.2s ease",
  color: theme.palette.text.primary,
}));

// Input card
export const CleanInputCard = styled(Card)(({ theme }) => ({
  background: theme.palette.background.paper,
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  borderRadius: "20px",
  boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.04)}`,
  transition: "all 0.2s ease",
  "&:focus-within": {
    border: `2px solid ${alpha(theme.palette.primary.main, 0.9)}`,
    boxShadow: `0 0px 20px ${alpha(theme.palette.primary.main, 0.8)}`,
  },
}));

// Toggle button
export const CleanToggle = styled(Box)(({ theme, enabled }) => ({
  padding: theme.spacing(0.75, 1.5),
  borderRadius: "12px",
  border: `1.5px solid ${
    enabled ? theme.palette.primary.main : alpha(theme.palette.divider, 0.2)
  }`,
  background: enabled ? alpha(theme.palette.primary.main, 0.08) : "transparent",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.75),
  transition: "all 0.2s ease",
  minWidth: "110px",
  justifyContent: "center",
  "&:hover": {
    borderColor: theme.palette.primary.main,
    background: enabled
      ? alpha(theme.palette.primary.main, 0.12)
      : alpha(theme.palette.primary.main, 0.04),
  },
}));

// Elapsed time chip
export const ElapsedTimeChip = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0.25, 0.75),
  borderRadius: "8px",
  background: alpha("#497DFF", 0.1),
  color: "#497DFF",
  fontSize: "10px",
  fontWeight: 600,
  border: "none",
}));

// Icon button
export const CleanIconButton = styled(IconButton)(
  ({ theme, variant = "default" }) => ({
    borderRadius: "10px",
    transition: "all 0.2s ease",
    ...(variant === "send" && {
      background: theme.palette.primary.main,
      color: "white",
      "&:hover": {
        background: theme.palette.primary.dark,
      },
      "&:disabled": {
        background: alpha(theme.palette.action.disabled, 0.12),
        color: theme.palette.action.disabled,
      },
    }),
    ...(variant === "stop" && {
      background: theme.palette.error.main,
      color: "white",
      "&:hover": {
        background: theme.palette.error.dark,
      },
    }),
  })
);

// File preview chip
export const FilePreviewChip = styled(Chip)(({ theme }) => ({
  borderRadius: "10px",
  background: alpha(theme.palette.primary.main, 0.08),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  maxWidth: "180px",
  "& .MuiChip-label": {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1),
  },
}));

// Image preview container
export const ImagePreviewContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  borderRadius: "12px",
  overflow: "hidden",
  maxWidth: "300px",
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  cursor: "pointer",
  transition: "all 0.2s ease",
  "&:hover": {
    boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.1)}`,
  },
}));

// PDF preview container
export const PDFPreviewContainer = styled(Box)(({ theme }) => ({
  borderRadius: "12px",
  padding: theme.spacing(2),
  background: alpha(theme.palette.error.main, 0.05),
  border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
  maxWidth: "300px",
  cursor: "pointer",
  transition: "all 0.2s ease",
  "&:hover": {
    background: alpha(theme.palette.error.main, 0.08),
    borderColor: alpha(theme.palette.error.main, 0.3),
  },
}));

// Gradient loading text
export const GradientLoadingText = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "12px",
  background: `linear-gradient(90deg, 
    transparent 0%,
    ${theme.palette.primary.main} 50%,
    transparent 100%
  )`,
  backgroundSize: "200% 100%",
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  animation: `${gradientSlide} 2s linear infinite`,
}));

// Analysis option card
export const AnalysisOptionCard = styled(Box)(({ theme, isSelected }) => ({
  padding: theme.spacing(2),
  borderRadius: "12px",
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.primary.main, isSelected ? 0.25 : 0.05)} 0%, 
    ${alpha(theme.palette.primary.main, 0.05)} 100%
  )`,
  border: `1.5px solid ${
    isSelected
      ? alpha(theme.palette.primary.main, 0.3)
      : alpha(theme.palette.divider, 0.3)
  }`,
  cursor: "pointer",
  transition: "all 0.2s ease",
  "&:hover": {
    borderColor: theme.palette.primary.main,
    background: `linear-gradient(135deg, 
      ${alpha(theme.palette.primary.main, isSelected ? 0.3 : 0.1)} 0%, 
      ${alpha(theme.palette.primary.main, 0.08)} 100%
    )`,
  },
}));

const auroraPulse = keyframes`
  0%, 100% { 
    opacity: 0.7;
    transform: scaleY(0.6) scaleX(1.2);
    filter: blur(40px) brightness(1.5);
  }
  50% { 
    opacity: 0.95;
    transform: scaleY(0.8) scaleX(1.4);
    filter: blur(45px) brightness(1.8);
  }
`;

const auroraWave = keyframes`
  0%, 100% { 
    transform: translateX(-15%);
  }
  50% { 
    transform: translateX(15%);
  }
`;

const auroraShimmer = keyframes`
  0%, 100% { 
    opacity: 0.3;
  }
  50% { 
    opacity: 0.6;
  }
`;

export const TopLoader = styled(Box)(({ theme }) => ({
  position: "fixed",
  left: 0,
  right: 0,
  height: "100px",
  zIndex: 10,
  pointerEvents: "none",
  overflow: "hidden",
  display: "flex",
  justifyContent: "center",

  "&::before": {
    content: '""',
    position: "absolute",
    width: "120%",
    height: "20px",
    background: `linear-gradient(90deg,
      transparent 0%,
      ${alpha(theme.palette.primary.main, 0.5)} 20%,
      ${alpha(theme.palette.secondary.main, 0.9)} 50%,
      ${alpha(theme.palette.primary.main, 0.5)} 80%,
      transparent 100%
    )`,
    animation: `${auroraWave} 5s ease-in-out infinite, ${auroraShimmer} 2s ease-in-out infinite`,
    filter: "blur(25px)",
  },

  "&::after": {
    content: '""',
    width: "60%",
    height: "15px",
    background: `radial-gradient(ellipse 150% 50% at center, 
      ${alpha(theme.palette.primary.light, 0.9)} 0%, 
      ${alpha(theme.palette.primary.main, 0.6)} 35%,
      transparent 70%
    )`,
    borderRadius: "50%",
    animation: `${auroraPulse} 2s ease-in-out infinite`,
  },
}));
